import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "../store"

interface SettingsState {
	selectedCalendar: string
	selectedCalendarName: string
	calendarInitialized: boolean
	calendarTabSetting: string | null
	calendarEventTypeFilter: string[]
	i18nextLng: string
	// Set from calendar UI as a Date or ISO string depending on caller
	selectedDate: string | Date | null
	ordering: Record<string, unknown[]>
	terminology: string
	// Combo entries have loosely-typed shapes across calendar variants; kept permissive
	calendarCombo: any[]
	calendarPersonalOnly: boolean
	calendarScrollTop: number | undefined
	// Selected via RootGroupSelector (value prop typed string); starts undefined until auto-selected
	adminTargetRootGroupId: any
}

const initialState: SettingsState = {
	selectedCalendar: "own",
	selectedCalendarName: "Oma kalenteri",
	calendarInitialized: false,
	calendarTabSetting: null,
	calendarEventTypeFilter: [],
	i18nextLng: "",
	selectedDate: null,
	// Personal client-side keyed list ordering
	ordering: { group: [] },
	// Expected values: "admin", "sport", "general"
	terminology: "general",
	// [{ type: <"group"|"child"|"own">, id: <ObjectId> }]
	calendarCombo: [],
	calendarPersonalOnly: false,
	calendarScrollTop: undefined,
	adminTargetRootGroupId: undefined,
}

export const settingsSlice = createSlice({
	name: "settings",
	initialState,
	reducers: {
		setCalendarScrollTop(state, { payload }: PayloadAction<number | undefined>) {
			state.calendarScrollTop = payload
		},
		setSelectedCalendar(state, { payload }: PayloadAction<string>) {
			state.selectedCalendar = payload
		},
		setCalendarTabSetting(state, { payload }: PayloadAction<string | null>) {
			state.calendarTabSetting = payload
		},
		setCalendarEventTypeFilter(state, { payload }: PayloadAction<string[]>) {
			state.calendarEventTypeFilter = payload
		},
		toggleCalendarEventTypeFilter(state, { payload }: PayloadAction<string>) {
			if (!state.calendarEventTypeFilter) {
				state.calendarEventTypeFilter = [payload]
			} else if (state.calendarEventTypeFilter.includes(payload)) {
				state.calendarEventTypeFilter = state.calendarEventTypeFilter.filter((t) => t !== payload)
			} else {
				state.calendarEventTypeFilter = [...state.calendarEventTypeFilter, payload]
			}
		},
		setSelectedDate(state, { payload }: PayloadAction<string | Date | null>) {
			state.selectedDate = payload
		},
		setCalendarCombo(state, { payload }: PayloadAction<any[]>) {
			state.calendarCombo = payload
		},
		setCalendarPersonalOnly(state, { payload }: PayloadAction<boolean>) {
			state.calendarPersonalOnly = payload
		},
		setAdminTargetRootGroupId(state, { payload }: PayloadAction<any>) {
			state.adminTargetRootGroupId = payload
		},
	},
})
export const {
	setSelectedCalendar,
	setCalendarTabSetting,
	setCalendarEventTypeFilter,
	toggleCalendarEventTypeFilter,
	setSelectedDate,
	setCalendarCombo,
	setCalendarPersonalOnly,
	setCalendarScrollTop,
	setAdminTargetRootGroupId,
} = settingsSlice.actions
export const settingsSelector = (state: RootState) => state.settings
